import type { Href } from "expo-router"
import { Link, useSegments } from "expo-router"
import React from "react"

import * as Models from "@/redux/models"
import { useRootSelector } from "@/redux/useRootSelector"
import { IconButton, Row } from "@/theme"
import { createSelector } from "@reduxjs/toolkit"

import type { IconButtonStyleProps } from "@/theme"

export type CycleDirection = "left" | "right"

interface DeviceCyclerProps extends Models.CodaDeviceAliasProps {
  isDisabled?: boolean
}

const getAliases = createSelector(
  Models.deviceConfiguration.selectAll,
  (devices) => devices.map((device) => device.codaDeviceAlias),
)

export function getAdjacentIds<T extends number | string>({
  currentId,
  ids,
}: {
  currentId: T | undefined
  ids: T[]
}) {
  if (typeof currentId === "undefined") {
    return null
  }
  const currentIndex = ids.indexOf(currentId)
  if (currentIndex === -1) {
    return null
  }
  if (ids.length < 2) {
    return null
  }

  let indexRight = currentIndex + 1
  if (indexRight >= ids.length) {
    indexRight = 0
  }
  const idRight = ids[indexRight]

  let indexLeft = currentIndex - 1
  if (indexLeft <= 0) {
    indexLeft = ids.length - 1
  }
  const idLeft = ids[indexLeft]

  if (typeof idLeft === "undefined") {
    throw new TypeError(`Id left is undefined`)
  }
  if (typeof idRight === "undefined") {
    throw new TypeError(`Id right is undefined`)
  }

  return {
    idLeft,
    idRight,
    indexLeft,
    indexRight,
  }
}
/**
 * Allows user to navigate cyclically through their devices
 */
export function DeviceCycler({
  codaDeviceAlias,
  isDisabled,
}: DeviceCyclerProps): React.JSX.Element | null {
  const numDevices = useRootSelector(Models.deviceConfiguration.selectTotal)
  const pathname = useSegments().join("/")
  const aliases = useRootSelector(getAliases)
  const nextIds = getAdjacentIds({ currentId: codaDeviceAlias, ids: aliases })
  // const currentIndex = aliases.indexOf(codaDeviceAlias)
  // if (aliases.length < 2) {
  //   return null
  // }
  // if (currentIndex === -1) {
  //   return null
  // }
  // let aliasLeft: string | undefined
  // if (currentIndex === 0) {
  //   aliasLeft = aliases[aliases.length - 1]
  // } else {
  //   aliasLeft = aliases[currentIndex - 1]
  // }

  // let aliasRight: string | undefined
  // if (currentIndex === aliases.length - 1) {
  //   aliasRight = aliases[0]
  // } else {
  //   aliasRight = aliases[currentIndex + 1]
  // }

  if (nextIds === null) {
    return null
  }

  const commonProps: IconButtonStyleProps = {
    isDisabled: numDevices === 0 || isDisabled,
    size: "sm",
    variant: "subtle",
  }

  return (
    <Row flexWrap={"nowrap"} space={"$2"}>
      <Link
        asChild
        href={
          {
            params: { codaDeviceAlias: nextIds.idLeft },
            pathname,
          } as Href
        }
      >
        <IconButton
          {...commonProps}
          IconComponent={"ArrowLeft"}
          id={"previousDeviceBtn"}
        />
      </Link>
      <Link
        asChild
        href={
          {
            params: { codaDeviceAlias: nextIds.idRight },
            pathname,
          } as Href
        }
      >
        <IconButton
          {...commonProps}
          IconComponent={"ArrowRight"}
          id={"nextDeviceBtn"}
        />
      </Link>
    </Row>
  )
}
